import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { fetchMe } from '@/lib/auth';
import { Avatar } from '@/components/ui/avatar';
import { useMarketingByAgent } from '@/hooks/data/useMarketingInsights';

export const Route = createFileRoute('/_app/marketing-admin')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me || me.profile.role !== 'admin') throw redirect({ to: '/' });
  },
  component: MarketingAdminPage,
});

function MarketingAdminPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useMarketingByAgent();
  const agents = data?.agents ?? [];
  const totals = data?.totals;

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink">{t('madmin.title', 'Marketing-Admin')}</h2>
        <p className="text-sm text-ink-3">{t('madmin.subtitle', 'Marketing-Aktivität aller Makler im Überblick.')}</p>
      </header>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label={t('madmin.kpi_campaigns', 'Kampagnen')} value={totals?.campaigns} />
        <Kpi label={t('madmin.kpi_articles', 'Artikel')} value={totals?.articles} />
        <Kpi label={t('madmin.kpi_designs', 'Grafiken')} value={totals?.designs} />
        <Kpi label={t('madmin.kpi_tasks', 'Offene Aufgaben')} value={totals?.tasks_open} />
      </div>

      <div className="fm-table-wrap">
        <table className="fm-table">
          <thead>
            <tr>
              <th>{t('madmin.col_agent', 'Makler')}</th>
              <th>{t('madmin.kpi_campaigns', 'Kampagnen')}</th>
              <th>{t('madmin.kpi_articles', 'Artikel')}</th>
              <th>{t('madmin.kpi_designs', 'Grafiken')}</th>
              <th>{t('madmin.kpi_tasks', 'Offene Aufgaben')}</th>
              <th>{t('madmin.col_total', 'Gesamt')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="text-center text-ink-3">
                  {t('common.loading', 'Lädt …')}
                </td>
              </tr>
            )}
            {!isLoading && agents.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-ink-3">
                  {t('madmin.empty', 'Noch keine Marketing-Aktivität.')}
                </td>
              </tr>
            )}
            {agents.map((a) => (
              <tr key={a.agent_profile_id}>
                <td>
                  <span className="inline-flex items-center gap-2">
                    <Avatar
                      src={a.avatar_key ? `/auth/avatar/${a.agent_profile_id}?v=1` : null}
                      name={a.full_name}
                      size="sm"
                    />
                    <span className="font-medium text-ink">{a.full_name}</span>
                    <span className="text-[11px] uppercase text-ink-3">
                      {a.role === 'call_center' ? 'CC' : a.role === 'admin' ? 'Admin' : ''}
                    </span>
                  </span>
                </td>
                <td>{a.campaigns}</td>
                <td>{a.articles}</td>
                <td>{a.designs}</td>
                <td>{a.tasks_open}</td>
                <td className="font-semibold text-ink">{a.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Kpi({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="fm-kpi">
      <div className="text-2xl font-display text-ink">{value ?? '—'}</div>
      <div className="text-xs text-ink-3">{label}</div>
    </div>
  );
}
